import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { PagedRequest } from '../_models/common/paged-response.model';


@Injectable()
export class ActionService extends BaseService {
  public currUser: any = {};
 // var x = {}
  private messageSource = new BehaviorSubject(null);
  currentMessage = this.messageSource.asObservable();

  changeMessage(message) {
    if (message) {
      this.messageSource.next(message)
    }
  }

  
  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient, 
    private config: AppConfig, 
    private authService: AuthenticationService,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
    this.currUser = authService.userSessionData;
    
    
    this.apiUrl += this.apiUrl.endsWith("/") ? "" : "/";
  }

  /** Builds the opt-in paging query string for `GetActions`, or '' when paging was not requested. */
  private buildPagingQuery(paging?: PagedRequest): string {
    if (!paging) {
      return '';
    }
    const params = new URLSearchParams();
    params.set('paged', 'true');
    params.set('pageNumber', String(paging.pageNumber));
    params.set('pageSize', String(paging.pageSize));
    if (paging.sortField) {
      params.set('sortField', paging.sortField);
    }
    if (paging.sortDirection) {
      params.set('sortDirection', paging.sortDirection);
    }
    return '?' + params.toString();
  }

  /**
   * Retrieves the actions relevant to the current user (or, when `staffFilter` is true, this user's
   * own tutor actions via `GetActions/{userId}`, which is always unpaged).
   *
   * `paging` is opt-in: omit it (the default) to get today's full, unpaged array - this is what the
   * Student autocomplete builder in `shared/search/search.component.ts` relies on and it must keep
   * calling this with no third argument. Pass it only from the actions table, and only when no
   * Client/Tutor/ClientReferrer filter is active - see `actions.component.ts`.
   *
   * The Task filter no longer reads from here: it is built from `getAllActionTypes`, because deriving
   * it from the returned rows meant an empty result set left the user nothing to filter by (37488).
   */
  getActions(staffFilter: boolean, paging?: PagedRequest) {
    if (!staffFilter) {
      return this.http.get(this.apiUrl + 'api/actions/GetActions/' + this.buildPagingQuery(paging), this.prepareOptions(null)).pipe(
        map((data: any) => {

        return data.body;
      }), catchError(this.handleError('getActions'))
    );
    }
    else {
      return this.http.get(this.apiUrl + 'api/actions/GetActions/' + this.currUser.currentUser.id, this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getActions'))
    );
    }
  }

  /**
   * Missing submissions, read entirely from LeapDb and paged in SQL.
   * Replaces getMissingSubmissions, which composed the grid from an unbounded Orbit session fetch.
   */
  getMissingSubmissionsFast(paging: { pageNumber: number; pageSize: number; companyIds?: number[] }) {
    return this.http.post(this.apiUrl + 'api/TutorReport/MissingSubmissionsFast/', paging, this.prepareOptions(paging)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('getMissingSubmissionsFast'))
    );
  }

  getMissingSubmissions(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/getMissingSubmissions/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getMissingSubmissions'))
    );
  }

  getMissingSubmissionsSearch(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/getMissingSubmissionsSearch/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getMissingSubmissionsSearch'))
    );
  }

  /**
   * Searches actions. `paging` is opt-in: omit it (the default) to get today's full, unpaged array -
   * the Student autocomplete rebuild in `shared/search/search.component.ts` relies on this and must
   * keep calling with no third argument. Pass it only from the actions table, and only when no
   * Client/Tutor/ClientReferrer filter is active - the server falls back to the full set anyway if
   * one is, since those filters are only ever evaluated in the browser (see `actions.component.ts`).
   */
  getActionsSearch(data, staffFilter: boolean, paging?: PagedRequest) {
    data.userId = staffFilter ? this.currUser.currentUser.id : 0;
    if (paging) {
      data.paged = true;
      data.pageNumber = paging.pageNumber;
      data.pageSize = paging.pageSize;
      data.sortField = paging.sortField;
      data.sortDirection = paging.sortDirection;
    }
    return this.http.post(this.apiUrl + 'api/actions/getActionsSearch/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getActionsSearch'))
    );
  }

  createAction(data) {
    return this.http.post(this.apiUrl + 'api/actions/Create/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('createAction'))
    );
  }

  deleteAction(data) {
    return this.http.post(this.apiUrl + 'api/actions/Delete/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('deleteAction'))
    );
  }

  getActionTypes(data) {
    return this.http.post(this.apiUrl + 'api/actions/GetActionTypes/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {

        return data.body;
      }), catchError(this.handleError('getActionTypes'))
    );
  }

  /**
   * Every action type definition, irrespective of status - the vocabulary behind the Pending Actions
   * "Task" filter. Unlike `getActionTypes`, which filters by status ID, this asks for the whole set.
   *
   * The filter used to be derived in the browser from whatever rows the current search returned, so it
   * could only offer tasks already on screen and offered nothing at all when a search found nothing
   * (37488, 37294). This is small, static reference data - fetch it once and filter against it.
   */
  getAllActionTypes() {
    return this.http.get(this.apiUrl + 'api/actions/GetAllActionTypes/', this.prepareOptions(null)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('getAllActionTypes'))
    );
  }

}
